/** Incident type examples shown and used for voice/transcript matching. */

export const PERSONAL_AUTO_INCIDENT_TYPES = [
  "Animal",
  "Collision",
  "Fire",
  "Flood",
  "Glass",
  "Hail",
  "Theft",
  "Road side assistance",
  "Vandalism",
  "Single vehicle accident",
  "Wind and other",
] as const;

export const HOMEOWNERS_INCIDENT_TYPES = [
  "Animals",
  "Earth movement",
  "Falling object",
  "Fire",
  "Hail",
  "Liability",
  "Lightning",
  "Medical payment",
  "Mold",
  "Flood",
  "Theft",
  "Wind and other",
] as const;

type IncidentPattern = { label: string; pattern: RegExp };

const AUTO_INCIDENT_PATTERNS: IncidentPattern[] = [
  { label: "Animal", pattern: /\b(animal|deer|wildlife)\b/i },
  { label: "Collision", pattern: /\b(collision|crash|rear[- ]?ended|hit and run|(?:car|vehicle)\s+accident)\b/i },
  { label: "Fire", pattern: /\bfire\b/i },
  { label: "Flood", pattern: /\bflood(ing)?\b/i },
  { label: "Glass", pattern: /\b(glass|windshield|broken glass)\b/i },
  { label: "Hail", pattern: /\bhail\b/i },
  { label: "Theft", pattern: /\b(theft|stolen)\b/i },
  {
    label: "Road side assistance",
    pattern: /\b(road\s*side\s*assistance|roadside\s*assist|tow|breakdown)\b/i,
  },
  { label: "Vandalism", pattern: /\bvandal/i },
  {
    label: "Single vehicle accident",
    pattern: /\b(single\s*vehicle|one\s*car|ran off the road)\b/i,
  },
  { label: "Wind and other", pattern: /\b(wind|storm|tornado|hurricane)\b/i },
];

const HOME_INCIDENT_PATTERNS: IncidentPattern[] = [
  { label: "Animals", pattern: /\b(animal|animals|deer|wildlife)\b/i },
  {
    label: "Earth movement",
    pattern: /\b(earth\s*movement|earthquake|landslide|sinkhole)\b/i,
  },
  {
    label: "Falling object",
    pattern: /\b(falling\s*object|tree\s*fall|branch\s*fall)\b/i,
  },
  { label: "Fire", pattern: /\bfire\b/i },
  { label: "Hail", pattern: /\bhail\b/i },
  { label: "Liability", pattern: /\bliabilit/i },
  { label: "Lightning", pattern: /\b(lightning|lightening)\b/i },
  {
    label: "Medical payment",
    pattern: /\b(medical\s*payment|med\s*pay)\b/i,
  },
  { label: "Mold", pattern: /\bmold\b/i },
  { label: "Flood", pattern: /\bflood(ing)?\b/i },
  { label: "Theft", pattern: /\b(theft|stolen|burglar)\b/i },
  { label: "Wind and other", pattern: /\b(wind|storm|tornado|hurricane)\b/i },
];

export function isHomePolicyType(policyType: string): boolean {
  const v = policyType.trim().toLowerCase().replace(/\s+/g, " ");
  return (
    /\b(home\s*owner|homeowner|homeowners?|home|ho|dwelling|property)\b/.test(v) ||
    v.includes("homeowner")
  );
}

export function isAutoPolicyType(policyType: string): boolean {
  const v = policyType.trim().toLowerCase();
  return v.includes("auto") || v === "vehicle" || v === "car";
}

export function getIncidentTypeExamples(policyType: string): readonly string[] {
  if (isHomePolicyType(policyType)) return HOMEOWNERS_INCIDENT_TYPES;
  if (isAutoPolicyType(policyType)) return PERSONAL_AUTO_INCIDENT_TYPES;
  return PERSONAL_AUTO_INCIDENT_TYPES;
}

/** Match spoken/typed text to a canonical incident type for the policy line. */
export function matchIncidentTypeFromSpeech(
  text: string,
  policyType?: string
): string | null {
  const trimmed = text.trim();
  if (!trimmed || /\?/.test(trimmed)) return null;
  const lower = trimmed.toLowerCase();
  const examples = getIncidentTypeExamples(policyType || "Auto");
  const exact = examples.find((e) => e.toLowerCase() === lower);
  if (exact) return exact;

  const patterns = isHomePolicyType(policyType || "")
    ? HOME_INCIDENT_PATTERNS
    : AUTO_INCIDENT_PATTERNS;

  for (const { label, pattern } of patterns) {
    if (pattern.test(trimmed)) return label;
  }
  return null;
}

/**
 * Infer incident type from free-form description, even when the caller mixed in questions.
 */
export function inferIncidentTypeFromNarrative(
  text: string,
  policyType = ""
): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const narrativeSentences = trimmed
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((sentence) => {
      if (!sentence.endsWith("?")) return true;
      return /\b(?:car|vehicle)\s+accident\b/i.test(sentence);
    });

  const narrativeText = narrativeSentences.join(" ").replace(/\?/g, " ").trim();
  const source = narrativeText || trimmed.replace(/\?/g, " ").trim();
  if (!source) return null;

  const matched = matchIncidentTypeFromSpeech(source, policyType);
  if (matched) return matched;

  const lower = source.toLowerCase();
  if (
    /\b(?:car|vehicle)\s+accident\b/i.test(lower) ||
    /\brear[- ]?ended\b/i.test(lower) ||
    /\bhit\s+(?:me|my)\b/i.test(lower) ||
    /\banother\s+(?:car|driver|vehicle)\b/i.test(lower)
  ) {
    return "Collision";
  }

  return null;
}

/** A few examples for the voice agent to mention (not the full list). */
export function getIncidentTypeSamplesForAgent(
  policyType: string,
  count = 3
): string {
  const examples = [...getIncidentTypeExamples(policyType)];
  const preferred = isHomePolicyType(policyType)
    ? ["Fire", "Hail", "Theft", "Wind and other", "Flood", "Liability"]
    : ["Collision", "Hail", "Theft", "Fire", "Vandalism", "Wind and other"];
  const picked: string[] = [];
  for (const label of preferred) {
    if (examples.includes(label) && !picked.includes(label)) {
      picked.push(label);
    }
    if (picked.length >= count) break;
  }
  for (const label of examples) {
    if (picked.length >= count) break;
    if (!picked.includes(label)) picked.push(label);
  }
  return picked.slice(0, count).join(", ");
}
